import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const MIN_SPEED = 0;
const MAX_SPEED = 10;
const DEFAULT_SPEED = 1;
const DRAG_RESUME_DELAY = 1500;
const DRAG_THRESHOLD = 6;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * @type {number}
   */
  #speedPercent = DEFAULT_SPEED;

  /**
   * @type {boolean}
   */
  #draggable = true;

  /**
   * @type {boolean}
   */
  #userPaused = false;

  /**
   * @type {number | null}
   */
  #resumeTimer = null;

  /**
   * @type {boolean}
   */
  #isDragging = false;

  /**
   * @type {boolean}
   */
  #reducedMotion = false;

  /**
   * @type {boolean}
   */
  #hasInteracted = false;

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.#readSettings();
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.#applySpeedGate();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);

    if (this.#draggable) {
      this.#setupDrag();
      this.addEventListener('keydown', this.#onKeyDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('keydown', this.#onKeyDown);
    if (this.refs.wrapper) {
      this.refs.wrapper.removeEventListener('pointerdown', this.#onPointerDown);
    }
    this.#clearResumeTimer();
  }

  #readSettings() {
    const speedAttr = this.getAttribute('data-speed');
    if (speedAttr !== null) {
      const parsed = Number(speedAttr);
      if (Number.isFinite(parsed)) {
        this.#speedPercent = clamp(parsed, MIN_SPEED, MAX_SPEED);
      }
    }

    const draggableAttr = this.getAttribute('data-draggable');
    if (draggableAttr !== null) {
      this.#draggable = draggableAttr !== 'false';
    }
  }

  #onFocusIn = () => {
    if (this.#reducedMotion || this.#isDragging) return;
    this.#userPaused = true;
    this.#pauseAnimation();
  };

  #onFocusOut = () => {
    if (this.#reducedMotion) return;
    this.#userPaused = false;
    this.#playAnimation();
  };

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation || this.#userPaused || this.#isDragging) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    if (this.#userPaused || this.#isDragging) return;
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /**
   * When the speed setting is 0, keep the animation inert by holding
   * `data-disabled` until a non-zero speed is restored.
   */
  #applySpeedGate() {
    if (this.#speedPercent <= 0) {
      this.#userPaused = true;
      this.#pauseAnimation();
    } else if (this.#userPaused && !this.#isDragging) {
      this.#userPaused = false;
      this.#playAnimation();
    }
  }

  #pauseAnimation() {
    this.setAttribute('data-disabled', '');
  }

  #playAnimation() {
    this.removeAttribute('data-disabled');
  }

  #clearResumeTimer() {
    if (this.#resumeTimer !== null) {
      clearTimeout(this.#resumeTimer);
      this.#resumeTimer = null;
    }
  }

  #setupDrag() {
    this.refs.wrapper.addEventListener('pointerdown', this.#onPointerDown);
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (this.#reducedMotion) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.closest('a, button, input, select, textarea, [contenteditable="true"]')) {
      return;
    }

    /** @type {HTMLElement | null} */
    const wrapper = this.refs.wrapper;
    if (!wrapper) return;

    // Capture the pointer immediately on the wrapper so pointerup fires even
    // if the cursor leaves the marquee. For mouse, prevent text selection;
    // for touch, let touch-action: pan-y handle vertical scrolling naturally.
    wrapper.setPointerCapture(event.pointerId);
    if (event.pointerType !== 'touch') {
      event.preventDefault();
    }

    const startX = event.clientX;
    const startY = event.clientY;
    const startTime = performance.now();

    // Freeze the marquee at its current animated position so removing the
    // CSS animation doesn't snap the content back to the origin.
    const computedTransform = getComputedStyle(wrapper).transform;
    const matrix = new DOMMatrixReadOnly(computedTransform === 'none' ? undefined : computedTransform);
    const startTranslateX = matrix.m41;
    wrapper.style.transform = `translate3d(${startTranslateX}px, 0, 0)`;

    this.#clearResumeTimer();
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;

    this.#pauseAnimation();

    const controller = new AbortController();
    const { signal } = controller;
    /** @type {number} */
    let previous = startX;
    /** @type {number} */
    let velocity = 0;
    /** @type {boolean} */
    let moved = false;
    /** @type {number} */
    let translateX = startTranslateX;

    const applyTransform = (/** @type {number} */ x) => {
      wrapper.style.transform = `translate3d(${x}px, 0, 0)`;
    };

    const endDrag = () => {
      controller.abort();
      try {
        wrapper.releasePointerCapture(event.pointerId);
      } catch {
        // Capture may have already been released by the browser.
      }
      this.#isDragging = false;
      this.removeAttribute('dragging');
    };

    const onPointerMove = (/** @type {PointerEvent} */ e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const current = e.clientX;
      const rawDelta = current - previous;
      previous = current;

      if (!moved) {
        if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          // Vertical scroll — restore the animation and stop handling this gesture.
          endDrag();
          wrapper.style.transform = '';
          if (!this.#userPaused) {
            this.#playAnimation();
          }
          return;
        }
        moved = true;
        this.#isDragging = true;
        this.setAttribute('dragging', '');
        document.addEventListener('click', preventDefault, { once: true, signal });
      }

      const timeDelta = performance.now() - startTime || 1;
      velocity = (rawDelta / timeDelta) * 1000;
      translateX += rawDelta;
      applyTransform(translateX);
    };

    const onPointerEnd = () => {
      endDrag();

      if (!moved) {
        wrapper.style.transform = '';
        if (!this.#userPaused) {
          this.#playAnimation();
        }
        return;
      }

      this.#hasInteracted = true;
      this.#scheduleResume(translateX, velocity);
    };

    wrapper.addEventListener('pointermove', onPointerMove, { signal });
    wrapper.addEventListener('pointerup', onPointerEnd, { signal });
    wrapper.addEventListener('pointercancel', onPointerEnd, { signal });
  };

  /**
   * @param {number} finalTranslateX
   * @param {number} velocity
   */
  #scheduleResume(finalTranslateX, velocity) {
    if (this.#reducedMotion) {
      this.#resumeFromTranslate(finalTranslateX, velocity);
      return;
    }

    this.#resumeTimer = window.setTimeout(() => {
      this.#resumeFromTranslate(finalTranslateX, velocity);
    }, DRAG_RESUME_DELAY);
  }

  /**
   * @param {number} finalTranslateX
   * @param {number} velocity
   */
  #resumeFromTranslate(finalTranslateX, velocity) {
    const { wrapper } = this.refs;
    const { contentWidth } = this.#getDimensions();
    if (!contentWidth) {
      this.#clearInlineTransform();
      if (!this.#userPaused) {
        this.#playAnimation();
      }
      return;
    }

    const cycle = contentWidth + this.#getGap();
    const travel = velocity * 0.4;
    const release = finalTranslateX + travel;

    // Wrap to the animation's natural [-cycle, 0] range and pick the nearest
    // endpoint so the post-resume jump back to the keyframe origin is minimal.
    const mod = ((release % cycle) + cycle) % cycle;
    const target = mod <= cycle / 2 ? -mod : -(cycle - mod);

    this.#animateToTranslate(wrapper, target, () => {
      this.#clearInlineTransform();
      if (!this.#userPaused) {
        this.#playAnimation();
      }
    });
  }

  #getDimensions() {
    const { content, wrapper } = this.refs;
    const marqueeWidth = this.clientWidth || this.#marqueeWidth || 0;
    const contentWidth = content.scrollWidth / 2 || 0;
    return { marqueeWidth, contentWidth };
  }

  #getGap() {
    const styles = getComputedStyle(this.refs.content);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    return Number.isFinite(gap) ? gap : 0;
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {number} target
   * @param {() => void} onComplete
   */
  #animateToTranslate(wrapper, target, onComplete) {
    const computedTransform = getComputedStyle(wrapper).transform;
    const matrix = new DOMMatrixReadOnly(computedTransform === 'none' ? undefined : computedTransform);
    const from = matrix.m41;

    if (this.#reducedMotion) {
      wrapper.style.transform = `translate3d(${target}px, 0, 0)`;
      onComplete();
      return;
    }

    const duration = 400;
    const startTime = performance.now();
    let cancelled = false;

    /**
     * @param {number} now
     */
    const step = (now) => {
      if (cancelled) return;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (target - from) * eased;
      wrapper.style.transform = `translate3d(${current}px, 0, 0)`;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        onComplete();
      }
    };

    requestAnimationFrame(step);

    this.#resumeTimer = window.setTimeout(() => {
      cancelled = true;
    }, duration + 100);
  }

  #clearInlineTransform() {
    this.refs.wrapper.style.transform = '';
    this.#clearResumeTimer();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (this.#reducedMotion) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#draggable) return;

    event.preventDefault();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    const width = this.clientWidth || this.getBoundingClientRect().width || 600;
    const delta = direction * width * 0.5;
    this.#nudge(delta);
  };

  /**
   * @param {number} delta
   */
  #nudge(delta) {
    this.#clearResumeTimer();
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    this.#pauseAnimation();

    const { wrapper } = this.refs;
    const computedTransform = getComputedStyle(wrapper).transform;
    const matrix = new DOMMatrixReadOnly(computedTransform === 'none' ? undefined : computedTransform);
    const from = matrix.m41;
    const target = from + delta;

    this.#animateToTranslate(wrapper, target, () => {
      this.#clearInlineTransform();
      if (!this.#userPaused) {
        this.#playAnimation();
      }
    });
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * Maps a 0-10 speed multiplier to an animation duration in seconds.
   * 0 stops the animation. 1.0 = default. Lower = slower, higher = faster.
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const baseFactor = 25;
    const speedFactor = Math.sqrt(numberOfCopies) * baseFactor;
    if (this.#speedPercent <= 0) return 0;
    return speedFactor * (1 / this.#speedPercent);
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#applySpeedGate();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * @param {MouseEvent} event
 */
function preventDefault(event) {
  event.preventDefault();
}

/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The animation duration in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
